import { MetricLineage } from "@/apps/AdminBetaApp/sections/companies/MetricsLab/MetricLineage";
import type { ParsedMetric } from "@/apps/AdminBetaApp/sections/companies/MetricsLab/metrics.types";
import type { Metric } from "@/entities/Finance/Finance.model";
import { useMemo, useState } from "react";
import { VariableIcon } from "@heroicons/react/24/outline";
import { InformationCircleIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";

interface MetricLineageDisplayProps {
    parsedMetric: ParsedMetric;
    metric: Metric;
}

export function MetricLineageDisplay({
    parsedMetric,
    metric,
}: MetricLineageDisplayProps) {
    const [showFormula, setShowFormula] = useState(false);

    const description = useMemo(() => {
        const fullDescription = metric.description?.description;
        if (!fullDescription) return undefined;
        const parts = fullDescription.split("\n\n");
        const out = parts.length >= 3 ? parts[1] : fullDescription;
        return out;
    }, [metric.description?.description]);

    const formula = useMemo(() => {
        const fullDescription = metric.description?.description;

        if (!fullDescription) return [];

        const afterExpression = fullDescription.split(
            "With the expression:\n",
        )[1];
        if (!afterExpression) return [];

        const codeBlockMatch = afterExpression.match(/```text\n([\s\S]*?)```/);
        if (!codeBlockMatch) return [];

        const formulaString = codeBlockMatch[1];
        const functionMatch = formulaString.match(/(\w+)\s*\(([\s\S]*)\)/);

        if (!functionMatch) {
            return [];
        }

        const [, , innerContent] = functionMatch;

        return innerContent
            .split(",")
            .map((operand) => operand.trim())
            .filter((operand) => operand && operand !== "");
    }, [metric.description?.description]);

    return (
        <div className="flex flex-col gap-4">
            {description && (
                <p className="body-s italic text-gray-400">{description}</p>
            )}

            {metric.description?.composition && (
                <p className="body-s flex items-center gap-1 capitalize italic text-gray-400">
                    {metric.description.composition}
                    <InformationCircleIcon className="size-4 fill-gray-500" />
                </p>
            )}

            {formula.length > 0 && (
                <div className="flex flex-col gap-2">
                    <p
                        className={clsx(
                            "body-s subdued flex cursor-pointer items-center gap-1",
                        )}
                        onClick={() => setShowFormula(!showFormula)}
                    >
                        {showFormula ? "Hide formula" : "Show formula"}{" "}
                        <VariableIcon className="size-4" />
                    </p>
                    {showFormula && (
                        <div
                            className={clsx(
                                "bg-[#263142] p-5",
                                "flex flex-wrap",
                                "gap-2 rounded",
                            )}
                        >
                            {formula.map((line) => {
                                return (
                                    <p className="py-1 font-[300]" key={line}>
                                        {line +
                                            (line ===
                                            formula[formula.length - 1]
                                                ? ""
                                                : " +")}
                                    </p>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            <MetricLineage metric={parsedMetric} />
        </div>
    );
}
